import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PortalNav } from "@/components/PortalNav";
import { CustomerMap } from "@/components/CustomerMap";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  ArrowLeft, Loader2, MapPin, Save, Locate, Briefcase, Phone, Plus, Trash2,
  FileText, ShieldCheck, Building2, DollarSign, CheckCircle2, Clock, Archive, UserCog, Send, Camera, ImagePlus, Wrench, Tag,
} from "lucide-react";
import { toast } from "sonner";

const TABS = ["overview", "credit", "deals", "calls", "equipment"];
const EVENT_TYPES = ["Phone Call", "Email", "In Person Visit", "Scheduled Visit"];

export default function CrmCustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canAssign = user?.role === "admin" || user?.role === "manager" || user?.role === "sales_manager";
  const [data, setData] = useState(null);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [sendingReq, setSendingReq] = useState(false);
  const [confirmConvert, setConfirmConvert] = useState(false);

  const sendCreditRequest = async () => {
    if (data?.customer?.is_prospect) {
      toast.error("This is a Prospect. Convert it to a Customer first to send a credit request.");
      return;
    }
    setSendingReq(true);
    try {
      const { data: res } = await api.post(`/crm/customers/${id}/send-credit-request`);
      toast.success(res.sent ? `Credit request sent to ${res.email}` : "Email sending failed — please try again");
      if (res.sent) load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally { setSendingReq(false); }
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/crm/customers/${id}`);
      setData(res.data);
    } catch {
      toast.error("Customer not found");
      navigate("/crm");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "manager" || user?.role === "sales_manager") api.get("/crm/reps").then(({ data }) => setReps(data.reps)).catch(() => {});
  }, [user]);

  const archive = async () => {
    try {
      const { data: res } = await api.post(`/crm/customers/${id}/archive`);
      toast.success(res.archived ? "Customer archived" : "Customer restored");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const assignRep = async (repId) => {
    try {
      await api.patch(`/crm/customers/${id}`, { assigned_rep_id: repId });
      toast.success("Sales rep updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const toggleProspect = async () => {
    const cur = data?.customer?.is_prospect;
    try {
      await api.patch(`/crm/customers/${id}`, { is_prospect: !cur });
      toast.success(!cur ? "Marked as Prospect" : "Converted to Customer");
      setConfirmConvert(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] md:pl-[var(--sb-w,16rem)]"><PortalNav />
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#0F172A]" /></div>
      </div>
    );
  }

  const c = data.customer;

  return (
    <div className="min-h-screen bg-[#F4F4F5] md:pl-[var(--sb-w,16rem)]">
      <PortalNav />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <button onClick={() => navigate("/crm")} data-testid="crm-back" className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#0F172A] transition-colors mb-4">
            <ArrowLeft className="h-4 w-4" /> Back to customers
          </button>

          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-2">Customer Profile</p>
              <h1 className={`font-display font-extrabold text-4xl tracking-tight flex items-center gap-3 ${c.is_prospect ? "text-[#2563EB]" : "text-[#0F172A]"}`}>
                {c.company_name}
                {c.is_prospect && <span data-testid="crm-profile-prospect-badge" className="text-[11px] uppercase tracking-wide text-white bg-[#2563EB] px-3 py-1 rounded-full">Prospect</span>}
                {c.archived && <span className="text-[11px] uppercase tracking-wide text-zinc-400 border border-zinc-300 px-2 py-1">Archived</span>}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-500">
                <span className="flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" /> {c.contact_name || "—"}</span>
                <span className="flex items-center gap-1.5 font-mono">{c.email}</span>
                <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {c.phone || "—"}</span>
                <span className="flex items-center gap-1.5"><UserCog className="h-3.5 w-3.5" /> {c.assigned_rep_name || c.sales_person || "Unassigned"}</span>
              </div>
              {c.credit_request_sent_at && (
                <p className="mt-2 text-xs text-[#B8860B] flex items-center gap-1.5" data-testid="crm-credit-request-sent">
                  <Send className="h-3 w-3" /> Last credit request sent {(c.credit_request_sent_at || "").slice(0, 10)}{c.credit_request_sent_by ? ` by ${c.credit_request_sent_by}` : ""}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setConfirmConvert(true)} data-testid="crm-toggle-prospect" className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${c.is_prospect ? "bg-[#2563EB] text-white hover:bg-[#1D4FD7]" : "border border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB]/10"}`}>
                <Tag className="h-4 w-4" /> {c.is_prospect ? "Convert to Customer" : "Mark as Prospect"}
              </button>
              <button onClick={sendCreditRequest} disabled={sendingReq || c.is_prospect} title={c.is_prospect ? "Convert this prospect to a customer to enable credit requests" : undefined} data-testid="crm-send-credit-request" className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${c.is_prospect ? "bg-zinc-200 text-zinc-400 cursor-not-allowed" : "bg-[#D4AF37] text-[#0F172A] hover:bg-[#C6A02E]"} disabled:opacity-60`}>
                {sendingReq ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Credit Request
              </button>
              {canAssign && (
                <>
                <select
                  value={c.assigned_rep_id || ""}
                  onChange={(e) => assignRep(e.target.value)}
                  data-testid="crm-assign-rep"
                  className="px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]"
                >
                  <option value="">Unassigned</option>
                  {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <button onClick={archive} data-testid="crm-archive-btn" className="flex items-center gap-2 px-4 py-2 text-sm border border-zinc-300 text-zinc-600 hover:border-[#EF4444] hover:text-[#EF4444] transition-colors">
                  <Archive className="h-4 w-4" /> {c.archived ? "Restore" : "Archive"}
                </button>
                </>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-8 border-b border-zinc-200 flex gap-1">
            {TABS.map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`crm-tab-${t}`}
                className={`px-4 py-3 text-sm capitalize border-b-2 -mb-px transition-colors ${
                  tab === t ? "border-[#D4AF37] text-[#0F172A] font-medium" : "border-transparent text-zinc-500 hover:text-[#0F172A]"
                }`}>
                {t === "credit" ? "Credit References" : t === "calls" ? "Event History" : t === "deals" ? "Deals & Transactions" : t === "equipment" ? "Equipment" : "Overview"}
              </button>
            ))}
          </div>

          <div className="mt-8">
            {tab === "overview" && <OverviewTab customer={c} onSaved={load} />}
            {tab === "credit" && <CreditTab submission={data.credit_submission} onOpenPortal={() => navigate("/admin")} />}
            {tab === "deals" && <DealsTab customerId={id} deals={data.deals} onChange={load} />}
            {tab === "calls" && <CallsTab customerId={id} calls={data.calls} onChange={load} />}
            {tab === "equipment" && <EquipmentTab customerId={id} />}
          </div>
        </motion.div>
      </main>

      {confirmConvert && (
        <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center px-4" onClick={() => setConfirmConvert(false)}>
          <div className="bg-white w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="crm-convert-confirm">
            <div className="px-6 py-4 border-b border-zinc-200">
              <h3 className="font-display font-bold text-[#0F172A]">{c.is_prospect ? "Convert to Customer?" : "Mark as Prospect?"}</h3>
            </div>
            <div className="px-6 py-5 text-sm text-zinc-600">
              {c.is_prospect
                ? <>This will convert <b>{c.company_name}</b> from a Prospect into a full Customer. The blue Prospect styling will be removed and credit requests will be enabled.</>
                : <>This will mark <b>{c.company_name}</b> as a Prospect. They'll appear in blue and credit requests will be disabled until converted back.</>}
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-3">
              <button onClick={() => setConfirmConvert(false)} data-testid="crm-convert-cancel" className="px-4 py-2 text-sm text-zinc-600 hover:text-[#0F172A]">Cancel</button>
              <button onClick={toggleProspect} data-testid="crm-convert-confirm-btn" className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors ${c.is_prospect ? "bg-[#0F172A] hover:bg-[#1E293B]" : "bg-[#2563EB] hover:bg-[#1D4FD7]"}`}>
                <Tag className="h-4 w-4" /> {c.is_prospect ? "Convert to Customer" : "Mark as Prospect"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Overview ---------------- */
function OverviewTab({ customer, onSaved }) {
  const [form, setForm] = useState({
    company_name: customer.company_name || "", contact_name: customer.contact_name || "",
    account_number: customer.account_number || "", notes: customer.notes || "",
    secondary_contact: customer.secondary_contact || "", secondary_phone: customer.secondary_phone || "", secondary_email: customer.secondary_email || "",
    sales_person: customer.sales_person || "", address: customer.address || "",
    city: customer.city || "", state: customer.state || "", zip_code: customer.zip_code || "",
    lat: customer.lat ?? "", lng: customer.lng ?? "",
    gps_approximate: customer.gps_approximate || false,
  });
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const latNum = form.lat === "" ? null : parseFloat(form.lat);
  const lngNum = form.lng === "" ? null : parseFloat(form.lng);
  const points = latNum != null && lngNum != null && !isNaN(latNum) && !isNaN(lngNum)
    ? [{ id: customer.id, lat: latNum, lng: lngNum, label: `<strong>${customer.company_name}</strong>` }] : [];

  const geocode = async () => {
    const q = [form.address, form.city, form.state, form.zip_code].filter(Boolean).join(", ");
    if (!q) { toast.error("Enter an address, city or zip first"); return; }
    setGeocoding(true);
    try {
      const { data } = await api.get("/crm/geocode", { params: { q } });
      setForm((f) => ({ ...f, lat: data.lat, lng: data.lng, gps_approximate: !!data.approximate }));
      if (data.approximate) {
        toast.warning("Exact address not found — dropped an approximate pin at the ZIP area. Please verify and adjust the GPS if needed.");
      } else {
        toast.success("Location found from address");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not find that address");
    } finally { setGeocoding(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/crm/customers/${customer.id}`, {
        company_name: form.company_name, contact_name: form.contact_name,
        account_number: form.account_number, notes: form.notes,
        secondary_contact: form.secondary_contact, secondary_phone: form.secondary_phone, secondary_email: form.secondary_email,
        sales_person: form.sales_person, address: form.address, city: form.city,
        state: form.state, zip_code: form.zip_code,
        gps_approximate: form.gps_approximate,
        lat: form.lat === "" ? null : parseFloat(form.lat),
        lng: form.lng === "" ? null : parseFloat(form.lng),
      });
      toast.success("Profile saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="bg-white border border-zinc-200 p-6 space-y-4">
        <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-[#D4AF37]" /><h2 className="font-display font-bold text-[#0F172A]">Details & Location</h2></div>
        <Field label="Customer Name" value={form.company_name} onChange={set("company_name")} testid="crm-company-name" />
        <Field label="Main Contact" value={form.contact_name} onChange={set("contact_name")} testid="crm-contact-name" />
        <Field label="Account Number" value={form.account_number} onChange={set("account_number")} testid="crm-account-number" />
        <Field label="Sales Person" value={form.sales_person} onChange={set("sales_person")} testid="crm-sales-person" />
        <div className="pt-2 border-t border-zinc-100">
          <p className="text-[11px] uppercase tracking-wide text-zinc-400 mb-3">Secondary Contact</p>
          <div className="space-y-4">
            <Field label="Secondary Contact" value={form.secondary_contact} onChange={set("secondary_contact")} testid="crm-secondary-contact" />
            <Field label="Secondary Phone" value={form.secondary_phone} onChange={set("secondary_phone")} testid="crm-secondary-phone" />
            <Field label="Secondary Email" type="email" value={form.secondary_email} onChange={set("secondary_email")} testid="crm-secondary-email" />
          </div>
        </div>
        <Field label="Address" value={form.address} onChange={set("address")} testid="crm-address" />
        <div className="grid grid-cols-3 gap-3">
          <Field label="City" value={form.city} onChange={set("city")} testid="crm-city" />
          <Field label="State" value={form.state} onChange={set("state")} testid="crm-state" />
          <Field label="Zip" value={form.zip_code} onChange={set("zip_code")} testid="crm-zip" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="GPS Latitude" value={form.lat} onChange={(v) => setForm((f) => ({ ...f, lat: v, gps_approximate: false }))} testid="crm-lat" />
          <Field label="GPS Longitude" value={form.lng} onChange={(v) => setForm((f) => ({ ...f, lng: v, gps_approximate: false }))} testid="crm-lng" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Notes</label>
          <textarea rows={3} value={form.notes} onChange={set("notes")} data-testid="crm-notes"
            placeholder="Add notes about this customer..."
            className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow resize-none" />
        </div>
        <div className="flex gap-3 pt-1">
          <button onClick={geocode} disabled={geocoding} data-testid="crm-geocode" className="flex items-center gap-2 px-4 py-2.5 text-sm border border-[#0F172A] text-[#0F172A] hover:bg-zinc-100 transition-colors disabled:opacity-60">
            {geocoding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Locate className="h-4 w-4" />} Get GPS from address
          </button>
          <button onClick={save} disabled={saving} data-testid="crm-save-overview" className="flex items-center gap-2 px-4 py-2.5 text-sm bg-[#0F172A] text-white hover:bg-[#1E293B] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
        </div>
      </div>
      <div>
        <div className="flex items-center gap-2 mb-3"><MapPin className="h-4 w-4 text-[#D4AF37]" /><h2 className="font-display font-bold text-[#0F172A]">Map</h2></div>
        <CustomerMap points={points} height={340} testid="crm-profile-map" />
        {points.length === 0 && <p className="mt-2 text-xs text-zinc-400">Add GPS coordinates (or use "Get GPS from address") to place this customer on the map.</p>}
      </div>
    </div>
  );
}

/* ---------------- Credit References ---------------- */
function CreditTab({ submission, onOpenPortal }) {
  return (
    <div className="bg-white border border-zinc-200">
      <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
        <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-[#D4AF37]" /><h2 className="font-display font-bold text-[#0F172A]">Credit References</h2></div>
      </div>
      {!submission ? (
        <div className="p-12 text-center text-zinc-400 text-sm" data-testid="crm-no-credit">
          This customer hasn't submitted a credit reference form yet in the Credit Portal.
        </div>
      ) : (
        <div className="p-6 space-y-6" data-testid="crm-credit-content">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <Info label="Signature" value={submission.signature} />
            <Info label="Net 30 agreed" value={submission.agreed_terms ? "Yes" : "No"} />
            <Info label="References" value={(submission.references || []).length} />
            <Info label="Updated" value={(submission.updated_at || "").slice(0, 10)} />
          </div>
          <div className="border border-zinc-200 divide-y divide-zinc-100">
            {(submission.references || []).map((r, i) => (
              <div key={`${r.token || r.email || "ref"}-${i}`} className="p-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                  <div><span className="text-zinc-400 text-xs block">Company</span>{r.company_name || "—"}</div>
                  <div><span className="text-zinc-400 text-xs block">Contact</span>{r.contact || "—"}</div>
                  <div><span className="text-zinc-400 text-xs block">Email</span><span className="font-mono text-xs break-all">{r.email || "—"}</span></div>
                </div>
                {r.token && (
                  <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${r.credit_status === "completed" ? "text-[#10B981]" : "text-zinc-400"}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${r.credit_status === "completed" ? "bg-[#10B981]" : "bg-zinc-300"}`} />
                    {r.credit_status === "completed" ? "Reference responded" : "Awaiting response"}
                  </span>
                )}
                {r.credit_data && (
                  <div className="border border-[#10B981]/30 bg-[#10B981]/5 p-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                    <Info label="Ref. company" value={r.credit_data.reference_company} small />
                    <Info label="Account length" value={r.credit_data.account_length} small />
                    <Info label="Terms" value={r.credit_data.terms} small />
                    <Info label="Current balance" value={r.credit_data.current_balance} small />
                    <Info label="Past due" value={r.credit_data.amount_past_due} small />
                    <Info label="High credit" value={r.credit_data.high_credit} small />
                    <Info label="30 days" value={r.credit_data.payment_30} small />
                    <Info label="60 days" value={r.credit_data.payment_60} small />
                    <Info label="90+ days" value={r.credit_data.payment_90} small />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Deals (draggable stage board) ---------------- */
const STAGES = ["Lead", "Qualified", "Proposal", "Won", "Lost"];
const dealStageTop = (s) => s === "Won" ? "border-t-[#10B981]" : s === "Lost" ? "border-t-[#EF4444]" : "border-t-[#D4AF37]";
function DealsTab({ customerId, deals, onChange }) {
  const [form, setForm] = useState({ title: "", stage: "Lead", amount: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState(deals || []);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  useEffect(() => { setItems(deals || []); }, [deals]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Deal title is required"); return; }
    setSaving(true);
    try {
      await api.post(`/crm/customers/${customerId}/deals`, {
        title: form.title, stage: form.stage, notes: form.notes,
        amount: form.amount === "" ? null : parseFloat(form.amount),
      });
      setForm({ title: "", stage: "Lead", amount: "", notes: "" });
      toast.success("Deal added");
      onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  const remove = async (dealId) => {
    const prev = items;
    setItems((xs) => xs.filter((d) => d.id !== dealId));
    try { await api.delete(`/crm/deals/${dealId}`); toast.success("Deal removed"); }
    catch (err) { setItems(prev); toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed"); }
  };
  const move = async (dealId, stage) => {
    const prev = items;
    setItems((xs) => xs.map((d) => (d.id === dealId ? { ...d, stage } : d)));
    try { await api.patch(`/crm/deals/${dealId}`, { stage }); toast.success(`Moved to ${stage}`); }
    catch (err) { setItems(prev); toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not move deal"); }
  };
  const drop = (stage) => { if (drag) move(drag, stage); setDrag(null); setOver(null); };
  const dealsIn = (s) => items.filter((d) => d.stage === s);
  const total = items.reduce((s, d) => s + (d.amount || 0), 0);

  return (
    <div className="space-y-6">
      <form onSubmit={add} className="bg-white border border-zinc-200 p-4 flex flex-wrap items-end gap-3" data-testid="crm-deal-form">
        <div className="flex items-center gap-2 mr-2"><DollarSign className="h-4 w-4 text-[#D4AF37]" /><span className="font-display font-bold text-[#0F172A] text-sm">Add Deal</span></div>
        <div className="flex-1 min-w-[160px]"><Field label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} testid="crm-deal-title" /></div>
        <div className="min-w-[130px]">
          <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Stage</label>
          <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} data-testid="crm-deal-stage"
            className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
            {STAGES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="w-28"><Field label="Amount ($)" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} testid="crm-deal-amount" /></div>
        <button type="submit" disabled={saving} data-testid="crm-deal-submit" className="flex items-center justify-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 text-sm font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Add</>}
        </button>
      </form>

      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-[#0F172A]">Deals & Transactions</h2>
        <span className="text-sm text-zinc-500">Pipeline: <span className="font-mono text-[#0F172A]">${total.toLocaleString()}</span></span>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-400 text-sm" data-testid="crm-no-deals">No deals yet. Add one above, then drag it across stages.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="crm-deal-board">
          {STAGES.map((s) => (
            <div key={s} onDragOver={(e) => { e.preventDefault(); setOver(s); }} onDrop={() => drop(s)}
              data-testid={`crm-deal-col-${s}`}
              className={`bg-zinc-50 border border-zinc-200 border-t-2 ${dealStageTop(s)} p-2 min-h-[140px] ${over === s ? "ring-2 ring-[#D4AF37]" : ""}`}>
              <div className="flex items-center justify-between px-1 pb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-zinc-600">{s}</span>
                <span className="text-[10px] text-zinc-400">{dealsIn(s).length}</span>
              </div>
              <div className="space-y-2">
                {dealsIn(s).map((d) => (
                  <div key={d.id} draggable onDragStart={() => setDrag(d.id)} onDragEnd={() => { setDrag(null); setOver(null); }}
                    data-testid={`crm-deal-card-${d.id}`}
                    className="border border-zinc-200 bg-white p-2.5 cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between gap-1">
                      <span className="font-medium text-sm text-[#0F172A] truncate">{d.title}</span>
                      <button onClick={() => remove(d.id)} onMouseDown={(e) => e.stopPropagation()} draggable={false} data-testid={`crm-deal-del-${d.id}`} className="text-zinc-300 hover:text-[#EF4444] transition-colors shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                    {d.amount != null && <div className="text-xs font-mono text-[#D4AF37] mt-1">${Number(d.amount).toLocaleString()}</div>}
                    {d.notes && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{d.notes}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Calls ---------------- */
function CallsTab({ customerId, calls, onChange }) {
  const [form, setForm] = useState({ contact: "", outcome: "", notes: "", call_date: "", follow_up_date: "", event_type: "Phone Call", visit_time: "" });
  const [saving, setSaving] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    if (!form.notes.trim() && !form.outcome.trim()) { toast.error("Add a note or outcome"); return; }
    setSaving(true);
    try {
      await api.post(`/crm/customers/${customerId}/calls`, form);
      setForm({ contact: "", outcome: "", notes: "", call_date: "", follow_up_date: "", event_type: "Phone Call", visit_time: "" });
      toast.success("Event logged");
      onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  const remove = async (callId) => { await api.delete(`/crm/calls/${callId}`); toast.success("Event removed"); onChange(); };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <form onSubmit={add} className="bg-white border border-zinc-200 p-6 space-y-4 lg:col-span-1 h-fit" data-testid="crm-call-form">
        <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#D4AF37]" /><h2 className="font-display font-bold text-[#0F172A]">Add an Event</h2></div>
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Event Type</label>
          <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} data-testid="crm-call-type"
            className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]">
            {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <Field label="Contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} testid="crm-call-contact" />
        <Field label="Date" type="date" value={form.call_date} onChange={(e) => setForm({ ...form, call_date: e.target.value })} testid="crm-call-date" />
        {!["Phone Call", "Email"].includes(form.event_type) && <Field label="Visit time (optional)" type="time" value={form.visit_time} onChange={(e) => setForm({ ...form, visit_time: e.target.value })} testid="crm-call-time" />}
        <Field label="Follow-up date (optional)" type="date" value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} testid="crm-call-followup" />
        <Field label="Outcome" value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} testid="crm-call-outcome" />
        <Field label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} testid="crm-call-notes" />
        <button type="submit" disabled={saving} data-testid="crm-call-submit" className="w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white py-2.5 text-sm font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Log event</>}
        </button>
      </form>
      <div className="lg:col-span-2">
        <div className="bg-white border border-zinc-200">
          <div className="p-5 border-b border-zinc-200"><h2 className="font-display font-bold text-[#0F172A]">Event History</h2></div>
          {calls.length === 0 ? (
            <div className="p-12 text-center text-zinc-400 text-sm" data-testid="crm-no-calls">No events logged yet.</div>
          ) : (
            <div className="divide-y divide-zinc-100" data-testid="crm-calls-list">
              {calls.map((c) => (
                <div key={c.id} className="p-4 flex items-start gap-4">
                  <div className="h-8 w-8 bg-[#0F172A]/5 flex items-center justify-center shrink-0"><Phone className="h-4 w-4 text-[#0F172A]" /></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="font-medium text-[#0F172A]">{c.contact || c.event_type || "Event"}</span>
                      {c.event_type && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 border border-[#D4AF37] text-[#B8860B]" data-testid={`crm-call-type-${c.id}`}>{c.event_type}</span>}
                      {c.outcome && <span className="text-xs text-[#D4AF37]">· {c.outcome}</span>}
                    </div>
                    {c.notes && <p className="text-sm text-zinc-600 mt-0.5">{c.notes}</p>}
                    <p className="text-xs text-zinc-400 mt-1 flex items-center gap-1"><Clock className="h-3 w-3" /> {(c.call_date || "").slice(0, 10)}{c.visit_time ? ` · ${c.visit_time}` : ""}{c.logged_by_name ? ` · by ${c.logged_by_name}` : ""}</p>
                    {c.follow_up_date && (
                      <span className={`inline-flex items-center gap-1 mt-1.5 text-[11px] px-2 py-0.5 border ${c.follow_up_done ? "border-[#10B981] text-[#10B981]" : "border-[#D4AF37] text-[#B8860B]"}`}>
                        <CheckCircle2 className="h-3 w-3" /> Follow-up {(c.follow_up_date || "").slice(0, 10)}{c.follow_up_done ? " · done" : ""}
                      </span>
                    )}
                  </div>
                  <button onClick={() => remove(c.id)} data-testid={`crm-call-del-${c.id}`} className="text-zinc-400 hover:text-[#EF4444] transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- shared ---------------- */
function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#0F172A] mb-1.5">{label}</label>
      <input type={type} value={value} onChange={onChange} data-testid={testid}
        className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow" />
    </div>
  );
}
function Info({ label, value, small }) {
  return (
    <div>
      <span className={`text-zinc-400 block ${small ? "text-[10px] uppercase" : "text-xs"}`}>{label}</span>
      <span className="text-[#0F172A]">{value === "" || value == null ? "—" : value}</span>
    </div>
  );
}

/* ---------------- Equipment ---------------- */
function EquipmentTab({ customerId }) {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [desc, setDesc] = useState("");
  const [fileObj, setFileObj] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDesc, setEditDesc] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/crm/customers/${customerId}/equipment`);
      setAssets(data.assets || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load equipment");
    } finally { setLoading(false); }
  }, [customerId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!fileObj) { toast.error("Please choose or take a photo"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("description", desc);
      fd.append("file", fileObj);
      await api.post(`/crm/customers/${customerId}/equipment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Asset added");
      setDesc(""); setFileObj(null); setAdding(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setSaving(false); }
  };

  const remove = async (eid) => {
    try {
      await api.delete(`/crm/equipment/${eid}`);
      toast.success("Asset removed");
      load();
    } catch { toast.error("Failed to remove"); }
  };

  const saveEdit = async (eid) => {
    setSavingEdit(true);
    try {
      await api.patch(`/crm/equipment/${eid}`, { description: editDesc });
      toast.success("Asset updated");
      setEditingId(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed"); }
    finally { setSavingEdit(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Camera className="h-4 w-4 text-[#D4AF37]" /><h2 className="font-display font-bold text-[#0F172A]">Equipment</h2></div>
        {!adding && (
          <button onClick={() => setAdding(true)} data-testid="equipment-new-asset" className="flex items-center gap-2 px-4 py-2.5 text-sm bg-[#0F172A] text-white hover:bg-[#1E293B] transition-colors">
            <Plus className="h-4 w-4" /> New Asset
          </button>
        )}
      </div>

      {adding && (
        <div className="bg-white border border-zinc-200 p-6 space-y-4" data-testid="equipment-new-form">
          <h3 className="font-medium text-[#0F172A]">New Asset</h3>
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Description</label>
            <textarea rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="equipment-desc"
              placeholder="Describe the equipment / asset..."
              className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow resize-none" />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer border border-[#0F172A] text-[#0F172A] px-4 py-2.5 text-sm hover:bg-zinc-100 transition-colors">
              <Camera className="h-4 w-4" /> Take photo
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFileObj(e.target.files?.[0] || null)} data-testid="equipment-camera-input" />
            </label>
            <label className="flex items-center gap-2 cursor-pointer border border-zinc-300 text-zinc-600 px-4 py-2.5 text-sm hover:bg-zinc-100 transition-colors">
              <ImagePlus className="h-4 w-4" /> Upload photo
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setFileObj(e.target.files?.[0] || null)} data-testid="equipment-file-input" />
            </label>
            {fileObj && <span className="text-xs text-zinc-500 truncate max-w-[200px]">{fileObj.name}</span>}
          </div>
          <div className="flex gap-3">
            <button onClick={submit} disabled={saving} data-testid="equipment-save" className="flex items-center gap-2 px-4 py-2.5 text-sm bg-[#0F172A] text-white hover:bg-[#1E293B] transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save asset
            </button>
            <button onClick={() => { setAdding(false); setDesc(""); setFileObj(null); }} className="px-4 py-2.5 text-sm border border-zinc-300 text-zinc-500 hover:text-[#0F172A] transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[#0F172A]" /></div>
      ) : assets.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-400 text-sm" data-testid="equipment-empty">No equipment assets yet. Tap "New Asset" to add a photo.</div>
      ) : (
        <div className="space-y-3" data-testid="equipment-list">
          {assets.map((a) => (
            <div key={a.id} className="bg-white border border-zinc-200 p-4 flex gap-4" data-testid={`equipment-item-${a.id}`}>
              <AuthImage eid={a.id} alt={a.description || "asset"} onEnlarge={setLightbox} />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-zinc-400 flex items-center gap-1"><Clock className="h-3 w-3" /> {(a.created_at || "").slice(0, 10)}{a.created_by ? ` · ${a.created_by}` : ""}</p>
                {editingId === a.id ? (
                  <div className="mt-2 space-y-2">
                    <textarea rows={3} value={editDesc} onChange={(e) => setEditDesc(e.target.value)} data-testid={`equipment-edit-desc-${a.id}`} className="w-full px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] resize-none" />
                    <div className="flex gap-2">
                      <button onClick={() => saveEdit(a.id)} disabled={savingEdit} data-testid={`equipment-edit-save-${a.id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#0F172A] text-white hover:bg-[#1E293B] transition-colors disabled:opacity-60">{savingEdit ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save</button>
                      <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-xs border border-zinc-300 text-zinc-500 hover:text-[#0F172A] transition-colors">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-[#0F172A] mt-1 whitespace-pre-wrap break-words">{a.description || "—"}</p>
                )}
              </div>
              <div className="flex flex-col gap-2 self-start">
                {editingId !== a.id && <button onClick={() => { setEditingId(a.id); setEditDesc(a.description || ""); }} data-testid={`equipment-edit-${a.id}`} className="text-xs text-zinc-500 hover:text-[#0F172A] transition-colors">Edit</button>}
                <button onClick={() => navigate(`/service?new_wo=1&customer=${customerId}&title=${encodeURIComponent((a.description || "Service").slice(0, 60))}`)} data-testid={`equipment-create-wo-${a.id}`} className="flex items-center gap-1 text-xs text-[#0F172A] hover:text-[#D4AF37] transition-colors"><Wrench className="h-3.5 w-3.5" /> Work Order</button>
                <button onClick={() => remove(a.id)} data-testid={`equipment-del-${a.id}`} className="text-zinc-400 hover:text-[#EF4444] transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4 cursor-zoom-out" onClick={() => setLightbox(null)} data-testid="equipment-lightbox">
          <img src={lightbox} alt="Equipment enlarged" className="max-h-[90vh] max-w-[90vw] object-contain" />
          <button onClick={() => setLightbox(null)} data-testid="equipment-lightbox-close" className="absolute top-4 right-6 text-white/80 hover:text-white text-sm border border-white/40 px-3 py-1.5">Close</button>
        </div>
      )}
    </div>
  );
}

function AuthImage({ eid, alt, onEnlarge }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let active = true;
    api.get(`/crm/equipment/${eid}/image`, { responseType: "blob" })
      .then(({ data }) => { if (active) { url = URL.createObjectURL(data); setSrc(url); } })
      .catch(() => {});
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [eid]);
  return (
    <button type="button" onClick={() => src && onEnlarge && onEnlarge(src)} data-testid={`equipment-img-${eid}`} className="h-24 w-24 shrink-0 bg-zinc-100 border border-zinc-200 overflow-hidden flex items-center justify-center cursor-zoom-in">
      {src ? <img src={src} alt={alt} className="h-full w-full object-cover" /> : <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
    </button>
  );
}
